import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { LogoIcon } from "@/components/Logo";
import { LayoutDashboard, MessageCircle, Camera, LogOut, Home, ClipboardList, CalendarClock, User, Edit3, Check, X, Layers } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const NAV_COLORS = {
  "/home": { active: "text-indigo-600", bg: "bg-indigo-50", border: "border-indigo-200" },
  "/dashboard": { active: "text-purple-600", bg: "bg-purple-50", border: "border-purple-200" },
  "/doubt": { active: "text-rose-600", bg: "bg-rose-50", border: "border-rose-200" },
  "/test": { active: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200" },
  "/homework": { active: "text-sky-600", bg: "bg-sky-50", border: "border-sky-200" },
  "/flashcards": { active: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200" },
  "/exam-countdown": { active: "text-teal-600", bg: "bg-teal-50", border: "border-teal-200" },
};

export default function Navbar() {
  const { user, logout, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [profileOpen, setProfileOpen] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState("");
  const dropdownRef = useRef(null);
  const token = localStorage.getItem("token");

  const navItems = [
    { path: "/home", label: "Home", icon: Home },
    { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { path: "/doubt", label: "Doubt", icon: MessageCircle },
    { path: "/test", label: "Test", icon: ClipboardList },
    { path: "/homework", label: "HW Solver", icon: Camera },
    { path: "/flashcards", label: "Cards", icon: Layers },
    { path: "/exam-countdown", label: "Exam", icon: CalendarClock },
  ];

  const isActive = (path) => location.pathname === path;

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setProfileOpen(false);
        setEditingName(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSaveName = async () => {
    if (!newName.trim()) return;
    try {
      const res = await axios.put(`${API}/auth/profile`, { name: newName.trim() }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      login(token, res.data);
      toast.success("Name updated!");
      setEditingName(false);
    } catch {
      toast.error("Failed to update name");
    }
  };

  return (
    <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/95 border-b border-slate-200 shadow-sm" data-testid="app-navbar">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 h-12 sm:h-16 flex items-center justify-between">
        <div className="flex items-center gap-2 sm:gap-6">
          <button data-testid="navbar-logo" onClick={() => navigate("/home")} className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
            <LogoIcon className="w-6 h-6 sm:w-9 sm:h-9" />
            <span className="font-heading text-xs sm:text-lg font-extrabold">
              <span className="text-indigo-600">Ai Studdy</span>{" "}
              <span className="text-slate-800">Buddy</span>
            </span>
          </button>

          <div className="hidden md:flex items-center gap-1">
            {navItems.map((item) => {
              const colors = NAV_COLORS[item.path] || {};
              return (
                <button
                  key={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                  onClick={() => navigate(item.path)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold transition-all ${
                    isActive(item.path)
                      ? `${colors.bg} ${colors.active}`
                      : "text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <item.icon className="w-4 h-4" strokeWidth={2.5} />
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <div className="hidden sm:flex items-center gap-1.5 bg-amber-50 text-amber-600 border border-amber-200 px-3 py-1.5 rounded-full text-sm font-semibold" data-testid="navbar-xp">
            <span>&#9733;</span> {user?.xp || 0} XP
          </div>

          <div className="relative" ref={dropdownRef}>
            <button
              data-testid="navbar-avatar"
              onClick={() => { setProfileOpen(!profileOpen); setEditingName(false); }}
              className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center text-white font-bold text-xs sm:text-sm hover:shadow-lg transition-all cursor-pointer"
            >
              {user?.name?.[0]?.toUpperCase() || "S"}
            </button>

            {profileOpen && (
              <div className="absolute right-0 top-full mt-2 w-64 bg-white border border-slate-200 rounded-2xl shadow-2xl p-4 z-[100]" data-testid="profile-dropdown">
                <div className="flex items-center gap-3 mb-3 pb-3 border-b border-slate-100">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                    {user?.name?.[0]?.toUpperCase() || "S"}
                  </div>
                  <div className="min-w-0 flex-1">
                    {editingName ? (
                      <div className="flex items-center gap-1">
                        <input
                          data-testid="profile-name-input"
                          autoFocus
                          value={newName}
                          onChange={e => setNewName(e.target.value)}
                          onKeyDown={e => e.key === "Enter" && handleSaveName()}
                          className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2 py-1 text-sm text-slate-800 outline-none focus:border-indigo-500"
                          placeholder="Your full name"
                        />
                        <button onClick={handleSaveName} className="text-green-500 hover:text-green-600 p-1" data-testid="profile-name-save">
                          <Check className="w-4 h-4" />
                        </button>
                        <button onClick={() => setEditingName(false)} className="text-slate-300 hover:text-slate-500 p-1">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1">
                        <p className="text-sm font-bold text-slate-800 truncate" data-testid="profile-name">{user?.name || "Student"}</p>
                        <button
                          data-testid="profile-edit-name-btn"
                          onClick={() => { setEditingName(true); setNewName(user?.name || ""); }}
                          className="text-slate-300 hover:text-indigo-500 p-0.5 flex-shrink-0"
                        >
                          <Edit3 className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                    <p className="text-[11px] text-slate-400 truncate">{user?.email}</p>
                  </div>
                </div>

                <div className="space-y-1 mb-3">
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-400">Grade</span>
                    <span className="text-slate-700 font-semibold">{user?.grade || "—"}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-400">Board</span>
                    <span className="text-slate-700 font-semibold">{user?.board || "—"}</span>
                  </div>
                  {user?.stream && (
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-400">Stream</span>
                      <span className="text-slate-700 font-semibold">{user.stream}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-400">XP</span>
                    <span className="text-amber-500 font-semibold">{user?.xp || 0}</span>
                  </div>
                </div>

                <button
                  data-testid="profile-dashboard-btn"
                  onClick={() => { navigate("/dashboard"); setProfileOpen(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 transition-all mb-1"
                >
                  <LayoutDashboard className="w-4 h-4" /> My Dashboard
                </button>

                <button
                  data-testid="profile-logout-btn"
                  onClick={() => { logout(); navigate("/"); setProfileOpen(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold text-red-400 hover:text-red-500 hover:bg-red-50 transition-all"
                >
                  <LogOut className="w-4 h-4" /> Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile nav - colorful icons */}
      <div className="md:hidden flex items-center border-t border-slate-100 py-1 px-2 overflow-x-auto scrollbar-hide gap-1 bg-white" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}>
        {navItems.map((item) => {
          const colors = NAV_COLORS[item.path] || {};
          return (
            <button
              key={item.path}
              data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              onClick={() => navigate(item.path)}
              className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl text-[10px] font-bold transition-all flex-shrink-0 min-w-[52px] ${
                isActive(item.path) ? `${colors.active} ${colors.bg}` : "text-slate-400"
              }`}
            >
              <item.icon className="w-5 h-5" strokeWidth={2.5} />
              {item.label}
            </button>
          );
        })}
        <div className="flex-shrink-0 w-3" aria-hidden="true" />
      </div>
    </nav>
  );
}
